/**
 * Collaborator detail page for NaraHub.
 * Shows detailed information about a collaborator including contact info,
 * workspace role, and project associations.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceUrl } from "../lib/routes";
import {
  useWorkspaceCollaboratorDetail,
  useUpdateWorkspaceCollaborator,
  useRemoveWorkspaceCollaborator,
  useCreateInvite,
  useIsWorkspaceAdmin,
  useMyWorkspaceRole,
  useWorkspaceMemberLabels,
  useSetWorkspaceMemberLabel,
} from "../hooks/useWorkspaceMembers";
import { PageHeader } from "../components/layout/PageHeader";
import { WorkspaceRoleBadge, getRoleDescription } from "../components/WorkspaceRoleBadge";
import { InviteLinkResult } from "../components/invite/InviteLinkResult";
import { AgentBadge } from "../components/agent/AgentBadge";
import { AgentKeysSection } from "../components/agent/AgentKeysSection";
import { AgentRoleConsent } from "../components/agent/AgentRoleConsent";
import { isAgent, needsEditorConsent, roleChoices } from "../lib/agentMembers";
import { normalizePhoneNumber } from "../lib/phone";
import { callableErrorCode, reinviteTarget } from "../lib/invite";
import type { WorkspaceCollaboratorRole, CollaboratorProjectAssociation} from "../services/naraDoor";
import {
  Users,
  Mail,
  Phone,
  FolderKanban,
  Check,
  ArrowLeft,
  Trash2,
  Send,
  Loader2,
  AlertCircle,
} from "lucide-react";

const CHANGEABLE_ROLES: WorkspaceCollaboratorRole[] = ["admin", "editor", "viewer", "uploader"];

export function CollaboratorDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const urls = useWorkspaceUrl();
  const { data: collaboratorData, isLoading } = useWorkspaceCollaboratorDetail(
    activeWorkspace?.id,
    id
  );
  const updateRoleMutation = useUpdateWorkspaceCollaborator();
  const removeCollaboratorMutation = useRemoveWorkspaceCollaborator();
  const createInviteMutation = useCreateInvite();
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditorConsentOpen, setIsEditorConsentOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const myRole = useMyWorkspaceRole();
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useWorkspaceMemberLabels(activeWorkspace?.id, isWorkspaceAdmin);
  const setLabelMutation = useSetWorkspaceMemberLabel();
  const label = id ? labels?.[id] : undefined;
  const [labelForm, setLabelForm] = useState<{ name: string; phone: string; note: string } | null>(null);
  const [labelError, setLabelError] = useState<string | null>(null);

  const getDisplayName = () => {
    if (collaboratorData?.first_name && collaboratorData?.last_name) {
      return `${collaboratorData.first_name} ${collaboratorData.last_name}`;
    }
    if (collaboratorData?.first_name) {
      return collaboratorData.first_name;
    }
    return collaboratorData?.email || t("collaboratorDetail.unknown");
  };

  const getInitials = () => {
    if (collaboratorData?.first_name && collaboratorData?.last_name) {
      return `${collaboratorData.first_name[0]}${collaboratorData.last_name[0]}`.toUpperCase();
    }
    if (collaboratorData?.first_name) {
      return collaboratorData.first_name.substring(0, 2).toUpperCase();
    }
    if (collaboratorData?.email) {
      return collaboratorData.email.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  const sendRoleChange = (role: WorkspaceCollaboratorRole) => {
    if (!id || !activeWorkspace?.id) return;
    const workspaceId = activeWorkspace.id;
    updateRoleMutation.mutate(
      { workspaceId, userId: id, role },
      {
        onSuccess: () => setIsEditorConsentOpen(false),
        onError: (err) => console.error("updateWorkspaceCollaborator failed", { workspaceId, userId: id, code: callableErrorCode(err) }),
      }
    );
  };

  const handleRoleChange = (role: WorkspaceCollaboratorRole) => {
    setIsRoleDialogOpen(false);
    updateRoleMutation.reset();
    // Viewer → Editor lets an AI agent change content: ask for the consent again before the PATCH.
    if (needsEditorConsent(isAgent(collaboratorData), collaboratorData?.workspace_role ?? null, role)) {
      setIsEditorConsentOpen(true);
      return;
    }
    sendRoleChange(role);
  };

  const handleDelete = async () => {
    if (!id || !activeWorkspace?.id) return;
    try {
      await removeCollaboratorMutation.mutateAsync({
        workspaceId: activeWorkspace.id,
        userId: id,
      });
      if (urls) {
        navigate(urls.collaborators);
      }
    } catch (error) {
      // Error is handled by mutation
    }
  };

  const handleCreateInvitation = () => {
    if (!collaboratorData || !activeWorkspace?.id) return;
    setInviteError(null);
    const { first_name, last_name, email, phone, workspace_role } = collaboratorData;
    const target = reinviteTarget(workspace_role, collaboratorData.projects?.map((p) => p.project_id) ?? []);
    if (!target) {
      setInviteError(t("invite.errors.reinviteFromProject"));
      return;
    }
    createInviteMutation.mutate(
      {
        workspaceId: activeWorkspace.id,
        ...target,
        inviteeName: [first_name, last_name].filter(Boolean).join(" ") || undefined,
        inviteeEmail: email || undefined,
        inviteePhone: phone ? normalizePhoneNumber(phone) : undefined,
        send: true,
      },
      {
        onError: (err) => {
          console.error("createInvite failed", callableErrorCode(err));
          setInviteError(t("invite.errors.createFailed"));
        },
      }
    );
  };

  const handleEditLabel = () => {
    setLabelError(null);
    setLabelForm({ name: label?.label_name ?? "", phone: label?.label_phone ?? "", note: label?.label_note ?? "" });
  };

  const handleSaveLabel = (e: FormEvent) => {
    e.preventDefault();
    if (!id || !activeWorkspace?.id || !labelForm) return;
    setLabelError(null);
    const workspaceId = activeWorkspace.id;
    setLabelMutation.mutate(
      {
        workspaceId,
        userId: id,
        label: {
          label_name: labelForm.name.trim() || null,
          label_phone: labelForm.phone.trim() || null,
          label_note: labelForm.note.trim() || null,
        },
      },
      {
        onSuccess: () => setLabelForm(null),
        onError: (err) => {
          console.error("setWorkspaceMemberLabel failed", { workspaceId, userId: id, error: err.message });
          setLabelError(t("collaboratorDetail.privateDetails.saveError"));
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t("common.loading")}
          breadcrumbs={[
            { label: t("nav.workspaceSection") },
            { label: t("nav.collaborators"), href: urls?.collaborators },
            { label: "..." },
          ]}
        />
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="animate-pulse space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-muted" />
                <div className="space-y-2">
                  <div className="h-6 w-48 bg-muted rounded" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="h-16 bg-muted rounded" />
                <div className="h-16 bg-muted rounded" />
                <div className="h-16 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!collaboratorData) {
    return (
      <div>
        <PageHeader
          title={t("collaboratorDetail.notFound")}
          breadcrumbs={[
            { label: t("nav.workspaceSection") },
            { label: t("nav.collaborators"), href: urls?.collaborators },
            { label: t("collaboratorDetail.notFoundBreadcrumb") },
          ]}
        />
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t("collaboratorDetail.notFoundDescription")}</p>
          <button
            onClick={() => urls && navigate(urls.collaborators)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-blue-600 hover:text-blue-700 dark:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("collaboratorDetail.backToCollaborators")}
          </button>
        </div>
      </div>
    );
  }

  const currentRole = collaboratorData.workspace_role || null;
  // Only the owner gives the admin role (the server refuses it from an admin).
  const reinviteRole = reinviteTarget(collaboratorData.workspace_role, collaboratorData.projects?.map((p) => p.project_id) ?? [])?.role;
  const canReinvite = reinviteRole !== "admin" || myRole === "owner";
  const hasWorkspaceRole = currentRole !== null && currentRole !== "inactive";
  const displayName = getDisplayName();
  const agent = isAgent(collaboratorData);

  return (
    <div>
      <PageHeader
        title={displayName}
        breadcrumbs={[
          { label: t("nav.workspaceSection") },
          { label: t("nav.collaborators"), href: urls?.collaborators },
          { label: displayName },
        ]}
      />

      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <span className="text-2xl font-medium text-blue-700 dark:text-blue-300">
                {getInitials()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-foreground">
                {displayName}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <WorkspaceRoleBadge role={currentRole} />
                {agent && <AgentBadge />}
                {collaboratorData.is_on_any_project && (
                  <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
                    {t("collaboratorDetail.onProjects")}
                  </span>
                )}
                {currentRole !== "owner" && (
                  <button
                    type="button"
                    onClick={() => setIsRoleDialogOpen(true)}
                    disabled={updateRoleMutation.isPending}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 hover:underline disabled:opacity-50"
                  >
                    {t("collaboratorDetail.changeRole")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {updateRoleMutation.error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mt-4">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{updateRoleMutation.error.message}</p>
            </div>
          )}

          {/* Contact Info Grid: an AI agent has no email or phone */}
          {!agent && (
            <div className="grid gap-4 sm:grid-cols-2 mt-6">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">{t("collaboratorDetail.email")}</p>
                <p className="font-medium text-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {collaboratorData.email || "—"}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">{t("collaboratorDetail.phone")}</p>
                <p className="font-medium text-foreground flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  {collaboratorData.phone || "—"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* An AI agent's keys: admins only, like the routes */}
        {agent && isWorkspaceAdmin && activeWorkspace && id && (
          <AgentKeysSection workspaceId={activeWorkspace.id} agentUserId={id} />
        )}

        {/* Private Details: admin-only label on this seat */}
        {isWorkspaceAdmin && (
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">{t("collaboratorDetail.privateDetails.title")}</h3>
                <p className="text-sm text-muted-foreground">{t("collaboratorDetail.privateDetails.help")}</p>
              </div>
              {!labelForm && !labelsLoading && !labelsError && (
                <button
                  type="button"
                  onClick={handleEditLabel}
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300 hover:underline"
                >
                  {t("collaboratorDetail.privateDetails.edit")}
                </button>
              )}
            </div>

            {labelsError ? (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{t("collaboratorDetail.privateDetails.loadError")}</p>
              </div>
            ) : labelForm ? (
              <form onSubmit={handleSaveLabel} className="space-y-4">
                <label className="block">
                  <span className="text-sm text-muted-foreground">{t("collaboratorDetail.privateDetails.name")}</span>
                  <input
                    type="text"
                    value={labelForm.name}
                    maxLength={255}
                    onChange={(e) => setLabelForm({ ...labelForm, name: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-muted-foreground">{t("collaboratorDetail.privateDetails.phone")}</span>
                  <input
                    type="tel"
                    value={labelForm.phone}
                    maxLength={50}
                    onChange={(e) => setLabelForm({ ...labelForm, phone: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
                <label className="block">
                  <span className="text-sm text-muted-foreground">{t("collaboratorDetail.privateDetails.note")}</span>
                  <textarea
                    value={labelForm.note}
                    maxLength={2000}
                    rows={3}
                    placeholder={t("collaboratorDetail.privateDetails.notePlaceholder")}
                    onChange={(e) => setLabelForm({ ...labelForm, note: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </label>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setLabelForm(null)}
                    disabled={setLabelMutation.isPending}
                    className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                  >
                    {t("collaboratorDetail.privateDetails.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={setLabelMutation.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {setLabelMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t("collaboratorDetail.privateDetails.save")}
                  </button>
                </div>
              </form>
            ) : labelsLoading ? (
              <p className="text-muted-foreground">{t("common.loading")}</p>
            ) : label ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">{t("collaboratorDetail.privateDetails.name")}</p>
                  <p className="font-medium text-foreground">{label.label_name || "—"}</p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">{t("collaboratorDetail.privateDetails.phone")}</p>
                  <p className="font-medium text-foreground flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {label.label_phone || "—"}
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg sm:col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">{t("collaboratorDetail.privateDetails.note")}</p>
                  <p className="font-medium text-foreground whitespace-pre-wrap">{label.label_note || "—"}</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">{t("collaboratorDetail.privateDetails.empty")}</p>
            )}

            {labelError && (
              <div className="flex items-center gap-2 p-3 mt-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{labelError}</p>
              </div>
            )}
          </div>
        )}

        {/* Projects Section */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">
            {t("collaboratorDetail.projects")} ({collaboratorData.projects?.length || 0})
          </h3>
          {collaboratorData.projects && collaboratorData.projects.length > 0 ? (
            <div className="space-y-3">
              {collaboratorData.projects.map((project: CollaboratorProjectAssociation) => (
                <Link
                  key={project.project_id}
                  to={urls ? urls.project(project.project_id) : "#"}
                  className="block"
                >
                  <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      <FolderKanban className="h-5 w-5 text-muted-foreground" />
                      <span className="font-medium text-foreground">
                        {project.project_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.email_transcription && (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                          {t("collaboratorDetail.emailReport")}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              {t("collaboratorDetail.noProjects")}
            </p>
          )}
        </div>

        {/* Actions Section */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-medium text-foreground mb-4">{t("collaboratorDetail.actions")}</h3>
          <div className="space-y-4">
            {/* Legacy invited rows: their old link never worked, so issue a new invitation */}
            {collaboratorData.workspace_collaborator_status === "invited" && canReinvite && (
              <div className="p-4 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">{t("collaboratorDetail.newInvitation")}</p>
                    <p className="text-sm text-muted-foreground">
                      {t("collaboratorDetail.newInvitationDescription")}
                    </p>
                  </div>
                  {!createInviteMutation.data && (
                    <button
                      onClick={handleCreateInvitation}
                      disabled={createInviteMutation.isPending}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      {createInviteMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {t("collaboratorDetail.createInvitation")}
                    </button>
                  )}
                </div>
                {createInviteMutation.data && (
                  <InviteLinkResult invite={createInviteMutation.data} onDone={() => createInviteMutation.reset()} />
                )}
              </div>
            )}

            {inviteError && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <p className="text-sm text-red-800 dark:text-red-200">{inviteError}</p>
              </div>
            )}

            {/* Delete Collaborator - only show if not owner */}
            {currentRole !== "owner" && (
              <div className="flex items-center justify-between p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950">
                <div>
                  <p className="font-medium text-red-900 dark:text-red-100">{t("collaboratorDetail.removeCollaborator")}</p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {t("collaboratorDetail.removeCollaboratorDescription")}
                  </p>
                </div>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("collaboratorDetail.remove")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Role Change Dialog */}
      {isRoleDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                {t("collaboratorDetail.changeRoleTitle")}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {t("collaboratorDetail.changeRoleDescription")}
              </p>
              <div className="space-y-2">
                {roleChoices(agent, CHANGEABLE_ROLES).map((role) => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(role)}
                    disabled={updateRoleMutation.isPending || currentRole === role}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      currentRole === role
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                        : "border-border hover:bg-accent"
                    } disabled:opacity-50`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <WorkspaceRoleBadge role={role} />
                        {currentRole === role && (
                          <span className="text-xs text-muted-foreground">({t("collaboratorDetail.current")})</span>
                        )}
                      </div>
                      {currentRole === role && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      {getRoleDescription(role)}
                    </p>
                  </button>
                ))}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setIsRoleDialogOpen(false)}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Viewer → Editor consent for an AI agent */}
      {isEditorConsentOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground">{t("agent.consent.title")}</h2>
            <AgentRoleConsent
              name={displayName}
              pending={updateRoleMutation.isPending}
              error={updateRoleMutation.error?.message ?? null}
              onConfirm={() => sendRoleChange("editor")}
              onCancel={() => {
                setIsEditorConsentOpen(false);
                updateRoleMutation.reset();
              }}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              {t("collaboratorDetail.removeCollaboratorTitle")}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t("collaboratorDetail.removeCollaboratorConfirm", { name: displayName })}
            </p>

            {removeCollaboratorMutation.error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg mb-4">
                <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{removeCollaboratorMutation.error.message}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={removeCollaboratorMutation.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleDelete}
                disabled={removeCollaboratorMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {removeCollaboratorMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("collaboratorDetail.remove")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
